/**
 * /admin/billing - billing health (CHAP-175 / Phase 1N #147). Read-only, admin.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api.js';

interface BillingHealth {
  mrrCents: number;
  mrrFormatted: string;
  byStatus: Record<string, number>;
  activeCount: number;
  perTenant: Array<{
    slug: string;
    displayName: string;
    status: string;
    mrrCents: number;
    periodEnd: string | null;
    stripeSubscriptionId: string | null;
  }>;
  stripe: {
    configured: boolean;
    error: string | null;
    recentCharges: Array<{ id: string; amount: number; status: string; created: number }>;
  };
  generatedAt: string;
}

export default function AdminBilling() {
  const { data, isLoading, error, refetch, isRefetching } = useQuery<BillingHealth, Error>({
    queryKey: ['admin-billing'],
    queryFn: () => apiGet<BillingHealth>('/api/admin/billing-health'),
    refetchInterval: 5 * 60_000,
  });

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-navy text-xl sm:text-2xl font-bold">Billing health</h2>
          <p className="text-neutral text-sm mt-1">ChapterBuild on the 1116 LLC Stripe account (brand-filtered). Per CHAP-175.</p>
        </div>
        <button type="button" onClick={() => refetch()} disabled={isRefetching} className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 flex-shrink-0">
          {isRefetching ? '…' : 'Refresh'}
        </button>
      </header>

      {isLoading && <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-neutral">Loading…</div>}
      {error && <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">Failed: {error.message}</div>}

      {data && (
        <>
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
            <Stat label="MRR" value={data.mrrFormatted} />
            <Stat label="Active subscriptions" value={String(data.activeCount)} />
            <Stat label="Past due" value={String(data.byStatus.past_due ?? 0)} />
            <Stat label="Canceled" value={String(data.byStatus.canceled ?? 0)} />
          </section>

          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
            <h3 className="text-navy text-sm font-semibold mb-3">Subscriptions by tenant</h3>
            <div className="min-w-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-neutral">
                  <tr className="border-b border-gray-100">
                    <th className="pb-2 pr-3 font-medium">Tenant</th>
                    <th className="pb-2 pr-3 font-medium">Status</th>
                    <th className="pb-2 pr-3 text-right font-medium">MRR</th>
                    <th className="pb-2 font-medium">Renews</th>
                  </tr>
                </thead>
                <tbody>
                  {data.perTenant.map((t) => (
                    <tr key={t.slug} className="border-b border-gray-50">
                      <td className="py-1.5 pr-3"><span className="font-medium text-navy">{t.displayName}</span> <span className="font-mono text-xs text-neutral">/{t.slug}</span></td>
                      <td className="py-1.5 pr-3 text-xs">{t.status}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">${(t.mrrCents / 100).toLocaleString()}</td>
                      <td className="py-1.5 text-xs text-neutral">{t.periodEnd ? new Date(t.periodEnd).toLocaleDateString() : 'n/a'}</td>
                    </tr>
                  ))}
                  {data.perTenant.length === 0 && (
                    <tr><td colSpan={4} className="py-3 text-center text-neutral text-xs">No subscriptions yet (no paid tenants - Planning phase is free, Omega Zeta is complimentary).</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
            <h3 className="text-navy text-sm font-semibold mb-3">Recent Stripe charges (brand=chapterbuild)</h3>
            {!data.stripe.configured && <p className="text-xs text-neutral">Stripe key not configured in this environment.</p>}
            {data.stripe.configured && data.stripe.error && <p className="text-xs text-amber-700">Stripe query unavailable: {data.stripe.error}</p>}
            {data.stripe.configured && !data.stripe.error && data.stripe.recentCharges.length === 0 && (
              <p className="text-xs text-neutral">No charges yet.</p>
            )}
            {data.stripe.recentCharges.length > 0 && (
              <table className="w-full text-sm">
                <tbody>
                  {data.stripe.recentCharges.map((c) => (
                    <tr key={c.id} className="border-b border-gray-50">
                      <td className="py-1.5 font-mono text-xs">{c.id}</td>
                      <td className="py-1.5 text-right font-mono">${(c.amount / 100).toLocaleString()}</td>
                      <td className="py-1.5 pl-3 text-xs">{c.status}</td>
                      <td className="py-1.5 pl-3 text-xs text-neutral">{new Date(c.created * 1000).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 min-w-0">
      <div className="text-neutral text-xs">{label}</div>
      <div className="text-navy text-2xl font-bold mt-1 truncate">{value}</div>
    </div>
  );
}
